import { useState, useEffect } from 'react';
import { CircleAlert as AlertCircle } from 'lucide-react';
import Modal from '../common/Modal';
import { createStakeholder, updateStakeholder } from '../../lib/mavisApi';
import type { MavisStakeholder, StakeholderRole, EngagementLevel } from '../../types/mavis';
import { STAKEHOLDER_ROLE_LABELS } from '../../types/mavis';

// Exported so panel and page can share the same slim contact shape
export interface ContactOption {
  id: string;
  first_name: string;
  last_name: string;
  role: string;   // job title
  email: string;
}

const ROLES: StakeholderRole[] = [
  'unknown',
  'champion', 'economic_buyer', 'technical_buyer',
  'influencer', 'partner', 'detractor', 'blocker',
];

const ENGAGEMENT_LEVELS: EngagementLevel[] = ['strong', 'moderate', 'weak', 'unknown'];

const ENGAGEMENT_LABELS: Record<EngagementLevel, string> = {
  strong:   'Strong',
  moderate: 'Moderate',
  weak:     'Weak',
  unknown:  'Unknown',
};

interface FormState {
  contactId:        string;
  name:             string;
  title:            string;
  role_in_deal:     StakeholderRole | '';
  engagement_level: EngagementLevel | '';
  notes:            string;
}

function emptyForm(stakeholder?: MavisStakeholder | null): FormState {
  if (stakeholder) {
    return {
      contactId:        stakeholder.contact_id ?? '',
      name:             stakeholder.name,
      title:            stakeholder.title,
      role_in_deal:     stakeholder.role_in_deal,
      engagement_level: stakeholder.engagement_level,
      notes:            stakeholder.notes,
    };
  }
  return { contactId: '', name: '', title: '', role_in_deal: '', engagement_level: '', notes: '' };
}

interface Props {
  isOpen:         boolean;
  onClose:        () => void;
  onSaved:        (stakeholder: MavisStakeholder) => void;
  opportunityId:  string;
  orgId:          string;
  contacts:       ContactOption[];
  stakeholder?:   MavisStakeholder | null;   // present → edit mode
}

export default function MavisAddStakeholderModal({
  isOpen, onClose, onSaved, opportunityId, contacts, stakeholder,
}: Props) {
  const isEdit = stakeholder != null;
  const [form, setForm] = useState<FormState>(() => emptyForm(stakeholder));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Reset when the modal opens or the stakeholder to edit changes
  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm(stakeholder));
      setErrors({});
      setApiError('');
    }
  }, [isOpen, stakeholder]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // When a contact is chosen, optionally prefill name/title from the contact record
  const handleContactChange = (contactId: string) => {
    set('contactId', contactId);
    if (!contactId) return;
    const c = contacts.find(x => x.id === contactId);
    if (!c) return;
    const fullName = `${c.first_name} ${c.last_name}`.trim();
    if (!form.name || form.name === form.name) set('name', fullName);   // always prefill name
    if (!form.title && c.role) set('title', c.role);                    // prefill title if empty
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.role_in_deal) errs.role_in_deal = 'Role is required';
    if (!form.engagement_level) errs.engagement_level = 'Engagement level is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setApiError('');

    // The server derives org_id / opportunity_id from the opportunity.
    const payload: Record<string, unknown> = {
      contact_id:       form.contactId || null,
      name:             form.name.trim(),
      title:            form.title.trim(),
      role_in_deal:     form.role_in_deal,
      engagement_level: form.engagement_level,
      notes:            form.notes.trim(),
    };

    try {
      let result;
      if (isEdit && stakeholder) {
        const { stakeholder: updated } = await updateStakeholder(stakeholder.id, payload);
        result = updated;
      } else {
        const { stakeholder: created } = await createStakeholder(opportunityId, payload);
        result = created;
      }

      onSaved(result as unknown as MavisStakeholder);
    } catch (err) {
      setApiError(
        err instanceof Error ? err.message : 'Failed to save stakeholder. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (key: keyof FormState) =>
    `input-field ${errors[key] ? '!border-red-300 !ring-red-100' : ''}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Stakeholder' : 'Add Stakeholder'}
      size="md"
    >
      <div className="space-y-4">
        {/* Link to existing contact */}
        <div>
          <label className="block text-sm font-medium text-app-ink mb-1.5">
            Link to existing contact <span className="text-app-muted font-normal">(optional)</span>
          </label>
          <select
            value={form.contactId}
            onChange={e => handleContactChange(e.target.value)}
            className="input-field"
          >
            <option value="">— Not linked —</option>
            {contacts.map(c => (
              <option key={c.id} value={c.id}>
                {c.first_name} {c.last_name}{c.role ? ` · ${c.role}` : ''}{c.email ? ` (${c.email})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* Name */}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              className={fieldClass('name')}
              placeholder="Full name"
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Title */}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-app-ink mb-1.5">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              className="input-field"
              placeholder="e.g. VP of Engineering"
            />
          </div>

          {/* Role in deal */}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Role in Deal <span className="text-red-500">*</span>
            </label>
            <select
              value={form.role_in_deal}
              onChange={e => set('role_in_deal', e.target.value as StakeholderRole | '')}
              className={fieldClass('role_in_deal')}
            >
              <option value="">Select role</option>
              {ROLES.map(r => (
                <option key={r} value={r}>{STAKEHOLDER_ROLE_LABELS[r]}</option>
              ))}
            </select>
            {errors.role_in_deal && (
              <p className="text-xs text-red-600 mt-1">{errors.role_in_deal}</p>
            )}
          </div>

          {/* Engagement level */}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Engagement <span className="text-red-500">*</span>
            </label>
            <select
              value={form.engagement_level}
              onChange={e => set('engagement_level', e.target.value as EngagementLevel | '')}
              className={fieldClass('engagement_level')}
            >
              <option value="">Select engagement</option>
              {ENGAGEMENT_LEVELS.map(l => (
                <option key={l} value={l}>{ENGAGEMENT_LABELS[l]}</option>
              ))}
            </select>
            {errors.engagement_level && (
              <p className="text-xs text-red-600 mt-1">{errors.engagement_level}</p>
            )}
          </div>

          {/* Notes */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-app-ink mb-1.5">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              className="input-field resize-none"
              rows={3}
              placeholder="Context, observations, relationship notes…"
            />
          </div>
        </div>

        {apiError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{apiError}</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary flex-1"
          >
            {submitting ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save Changes' : 'Add Stakeholder')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
